import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'redux/reducers/auth';
import {
    fetchChatToken,
    getUserData,
    userChatToken,
    userIsLoadedSelector,
    userSelector,
} from 'redux/reducers/user';
import { useAppDispatch } from '../redux/store';
import UserUpdateModal from '../components/Forms/UserUpdateModal';
import updateUserWhoRegisteredByPhone from '../api/Auth/updateUserWhoRegisteredByPhone';

interface Props {
    showTempEmailModal?: boolean;
    updateUserEmail?: (newEmail: string, newLogin: string, newPassword: string) => Promise<void>;
}

export const UserContext = React.createContext<Props>({});

export function UserProvider({ children }: { children: React.ReactNode }) {
    const { isAuth } = useSelector(authSelector);
    const { userData } = useSelector(userSelector);
    const userLoaded = useSelector(userIsLoadedSelector);
    const chatToken = useSelector(userChatToken);
    const dispatch = useAppDispatch();

    const [showUserUpdateModal, setShowUserUpdateModal] = useState(false);

    // Function to check if email is temporary, meaning registration was done via phone and user needs update.
    const isTempEmail = useCallback((email: string): boolean => {
        return email.startsWith('temp_') && email.endsWith('1tul.com');
    }, []);

    // Function to update user who registered via phone
    const handleUpdateUserWhoRegisteredByPhone = useCallback(
        async (newEmail: string, newLogin: string, newPassword: string): Promise<void> => {
            if (!userData?.uuid) {
                throw new Error('User UUID not available');
            }

            try {
                // Call the API with all three parameters
                await updateUserWhoRegisteredByPhone(
                    userData.uuid,
                    newLogin,
                    newEmail,
                    newPassword,
                );

                // After successful update, refresh user data to get the updated email
                dispatch(getUserData());

                // Close the modal
                setShowUserUpdateModal(false);
            } catch (error) {
                console.error('Failed to update user profile:', error);
                throw error;
            }
        },
        [userData?.uuid, dispatch],
    );

    // Original useEffect for loading user data
    useEffect(() => {
        if (!isAuth) {
            return;
        }
        if (userLoaded) {
            return;
        }
        dispatch(getUserData());
        if (!chatToken) {
            dispatch(fetchChatToken());
        }
    }, [isAuth, userLoaded, dispatch, chatToken]);

    // NEW: Check for temporary email when user data is loaded
    useEffect(() => {
        if (isAuth && userData?.email && userLoaded) {
            if (isTempEmail(userData.email)) {
                setShowUserUpdateModal(true);
            }
        }
    }, [isAuth, userData, userLoaded, isTempEmail]);

    const contextValue: Props = {
        showTempEmailModal: showUserUpdateModal,
        updateUserEmail: handleUpdateUserWhoRegisteredByPhone,
    };

    return (
        <UserContext.Provider value={contextValue}>
            {children}
            {/* THE MODAL IS RENDERED HERE - it's always in the DOM but only visible when showTempEmailModal is true */}
            <UserUpdateModal
                open={showUserUpdateModal}
                onClose={() => setShowUserUpdateModal(false)}
                onSubmit={handleUpdateUserWhoRegisteredByPhone}
            />
        </UserContext.Provider>
    );
}
